import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Phone } from "lucide-react";
import Embers from "./Embers";
import { scrollToId } from "./Navbar";
import { useContent } from "../content";

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.2 } },
};
const line = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

export default function Hero() {
  const content = useContent();
  const HERO = content.hero;
  const LINES = HERO.lines && HERO.lines.length ? HERO.lines : ["Du bâtiment", "à la scène,", "l'excellence technique."];
  const CONTACT = content.contact;

  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="hero"
      ref={ref}
      className="relative flex min-h-screen items-center overflow-hidden bg-[#050505]"
      data-testid="hero-section"
    >
      {/* Parallax image layer */}
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
        <img
          src={HERO.image}
          alt="Effets spéciaux événementiel"
          className="h-full w-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-[#050505]/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-transparent to-transparent" />
      </motion.div>

      <Embers density={70} />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pt-28 sm:px-8">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1, duration: 0.8 }}
          className="mb-6 flex items-center gap-3"
        >
          <span className="h-px w-10 bg-brand-gradient" />
          <span className="text-xs font-light uppercase tracking-[0.3em] text-white/70">
            {HERO.eyebrow}
          </span>
        </motion.div>

        <motion.h1
          variants={container}
          initial="hidden"
          animate="show"
          className="font-head text-5xl font-bold uppercase leading-[0.95] tracking-tighter text-white sm:text-6xl md:text-7xl lg:text-8xl"
          data-testid="hero-title"
        >
          {LINES.map((l, i) => (
            <span key={i} className="block overflow-hidden py-1">
              <motion.span
                variants={line}
                className={`block ${i === 2 ? "text-gradient" : ""}`}
              >
                {l}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-8 max-w-xl text-base font-light leading-relaxed text-white/70 sm:text-lg"
          data-testid="hero-subtitle"
        >
          {HERO.subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
        >
          <button
            onClick={() => scrollToId("devis")}
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand-gradient px-8 py-4 text-sm font-semibold text-white transition-transform duration-300 hover:scale-105"
            data-testid="hero-devis-btn"
          >
            Demander un devis
            <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <button
            onClick={() => scrollToId("services")}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 px-8 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-white hover:text-black"
            data-testid="hero-services-btn"
          >
            Nos services
          </button>
          <a
            href={`tel:${CONTACT.phone_raw}`}
            className="inline-flex items-center gap-2 px-2 py-4 text-sm font-medium text-white/70 transition-colors hover:text-white"
            data-testid="hero-phone-link"
          >
            <Phone size={16} className="text-[#ff4d4d]" />
            {CONTACT.phone}
          </a>
        </motion.div>
      </motion.div>
    </section>
  );
}
